import { useState } from 'react';
import { ArrowUpDown, Home, Flame, Droplet, Wind, DoorClosed, Layers, Award } from 'lucide-react';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import type { ScenarioImprovement, ImprovementType } from '../../types/work-scenarios';

interface WorksSynthesisTableProps {
  improvements: ScenarioImprovement[];
}

const IMPROVEMENT_ICONS: Record<ImprovementType, any> = {
  windows: DoorClosed,
  wall_insulation: Layers,
  roof_insulation: Home,
  floor_insulation: Layers,
  heating: Flame,
  hot_water: Droplet,
  ventilation: Wind,
  heating_control: Flame,
};

const IMPROVEMENT_LABELS: Record<ImprovementType, string> = {
  windows: 'Fenêtres',
  wall_insulation: 'Isolation murs',
  roof_insulation: 'Isolation toiture',
  floor_insulation: 'Isolation plancher',
  heating: 'Chauffage',
  hot_water: 'Eau chaude',
  ventilation: 'Ventilation',
  heating_control: 'Régulation',
};

type SortField = 'roi' | 'cost' | 'savings';

export function WorksSynthesisTable({ improvements }: WorksSynthesisTableProps) {
  const [sortField, setSortField] = useState<SortField>('roi');
  const [sortAscending, setSortAscending] = useState(true);

  if (improvements.length === 0) {
    return null;
  }

  const improvementsWithROI = improvements.map((imp) => ({
    ...imp,
    roi: imp.estimated_cost && imp.cost_savings && imp.cost_savings > 0
      ? imp.estimated_cost / imp.cost_savings
      : Infinity,
  }));

  const sortedImprovements = [...improvementsWithROI].sort((a, b) => {
    let valueA: number;
    let valueB: number;

    if (sortField === 'roi') {
      valueA = a.roi;
      valueB = b.roi;
    } else if (sortField === 'cost') {
      valueA = a.estimated_cost || 0;
      valueB = b.estimated_cost || 0;
    } else {
      valueA = a.cost_savings || 0;
      valueB = b.cost_savings || 0;
    }

    return sortAscending ? valueA - valueB : valueB - valueA;
  });

  const bestROI = Math.min(...improvementsWithROI.map(imp => imp.roi).filter(roi => roi !== Infinity));

  const totals = {
    cost: improvements.reduce((sum, imp) => sum + (imp.estimated_cost || 0), 0),
    energySavings: improvements.reduce((sum, imp) => sum + (imp.energy_savings || 0), 0),
    costSavings: improvements.reduce((sum, imp) => sum + (imp.cost_savings || 0), 0),
  };

  const totalROI = totals.costSavings > 0 ? totals.cost / totals.costSavings : 0;

  function handleSort(field: SortField) {
    if (sortField === field) {
      setSortAscending(!sortAscending);
    } else {
      setSortField(field);
      setSortAscending(field === 'roi' || field === 'cost');
    }
  }

  function getROIBgColor(roi: number) {
    if (roi === Infinity || roi <= 0) return '';
    if (roi < 8) return 'bg-green-50';
    if (roi <= 15) return 'bg-white';
    return 'bg-orange-50';
  }

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">Synthèse financière des travaux</h3>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b-2 border-gray-200">
              <th className="text-left py-3 px-2 font-semibold text-gray-700">Type de travaux</th>
              <th className="text-left py-3 px-2 font-semibold text-gray-700 w-48">Équipement</th>
              <th className="text-right py-3 px-2 font-semibold text-gray-700">Surface/Qté</th>
              <th className="text-right py-3 px-2 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('cost')}
                  className="flex items-center gap-1 ml-auto hover:text-blue-600"
                >
                  Coût estimé
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-700">
                <div className="flex flex-col items-end">
                  <span>Économie énergie</span>
                  <span className="text-xs font-normal text-gray-500">(Énergie finale)</span>
                </div>
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('savings')}
                  className="flex items-center gap-1 ml-auto hover:text-blue-600"
                >
                  Économie €/an
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
              <th className="text-right py-3 px-2 font-semibold text-gray-700">
                <button
                  onClick={() => handleSort('roi')}
                  className="flex items-center gap-1 ml-auto hover:text-blue-600"
                >
                  ROI (ans)
                  <ArrowUpDown className="w-3 h-3" />
                </button>
              </th>
            </tr>
          </thead>
          <tbody>
            {sortedImprovements.map((improvement, index) => {
              const Icon = IMPROVEMENT_ICONS[improvement.improvement_type];
              const isBestROI = improvement.roi === bestROI && improvement.roi !== Infinity;
              const rowBgColor = getROIBgColor(improvement.roi);

              return (
                <tr
                  key={improvement.id}
                  className={`border-b border-gray-100 ${rowBgColor} hover:bg-gray-50 transition-colors`}
                >
                  <td className="py-3 px-2">
                    <div className="flex items-center gap-2">
                      <Icon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                      <span className="font-medium text-gray-900">
                        {IMPROVEMENT_LABELS[improvement.improvement_type]}
                      </span>
                      {isBestROI && (
                        <Badge variant="success" className="text-xs flex items-center gap-1">
                          <Award className="w-3 h-3" />
                          Meilleur ROI
                        </Badge>
                      )}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-gray-700">
                    <div className="max-w-[180px] truncate" title={improvement.after_description}>
                      {improvement.after_description.split('|')[0].trim()}
                    </div>
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    {improvement.affected_surface
                      ? `${Math.round(improvement.affected_surface)} m²`
                      : '—'}
                  </td>
                  <td className="py-3 px-2 text-right font-semibold text-gray-900">
                    {improvement.estimated_cost && improvement.estimated_cost > 0
                      ? `${improvement.estimated_cost.toLocaleString()} €`
                      : '—'}
                  </td>
                  <td className="py-3 px-2 text-right text-gray-700">
                    {improvement.energy_savings?.toLocaleString() || 0} kWh/an
                  </td>
                  <td className="py-3 px-2 text-right font-semibold text-green-700">
                    {improvement.cost_savings?.toLocaleString() || 0} €/an
                  </td>
                  <td className="py-3 px-2 text-right">
                    {improvement.roi !== Infinity && improvement.roi > 0 ? (
                      <span className={`font-semibold ${
                        improvement.roi < 8
                          ? 'text-green-700'
                          : improvement.roi <= 15
                          ? 'text-orange-700'
                          : 'text-red-700'
                      }`}>
                        {improvement.roi.toFixed(1)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-gray-300 bg-gray-50 font-bold">
              <td className="py-3 px-2" colSpan={3}>
                TOTAL
              </td>
              <td className="py-3 px-2 text-right text-gray-900">
                {totals.cost.toLocaleString()} €
              </td>
              <td className="py-3 px-2 text-right text-gray-900">
                {totals.energySavings.toLocaleString()} kWh/an
              </td>
              <td className="py-3 px-2 text-right text-green-700">
                {totals.costSavings.toLocaleString()} €/an
              </td>
              <td className="py-3 px-2 text-right">
                {totalROI > 0 ? (
                  <span className={`${
                    totalROI < 10
                      ? 'text-green-700'
                      : totalROI <= 20
                      ? 'text-orange-700'
                      : 'text-red-700'
                  }`}>
                    {totalROI.toFixed(1)}
                  </span>
                ) : (
                  <span className="text-gray-400">—</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div className="mt-4 space-y-3">
        <div className="flex flex-wrap gap-2 text-xs text-gray-600">
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
            <span>ROI rapide (&lt;8 ans)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-white border border-gray-200 rounded"></div>
            <span>ROI moyen (8-15 ans)</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-4 h-4 bg-orange-50 border border-orange-200 rounded"></div>
            <span>ROI long (&gt;15 ans)</span>
          </div>
        </div>
        <div className="text-xs text-gray-600 bg-gray-50 p-3 rounded border border-gray-200">
          <p className="font-medium mb-1">💡 À propos des économies d'énergie :</p>
          <p>
            Les économies affichées correspondent à l'<strong>énergie finale (EF)</strong>, c'est-à-dire l'énergie réellement consommée qui apparaît sur votre facture.
            Le DPE se base sur l'<strong>énergie primaire (EP)</strong> qui applique un coefficient de 1.9 pour l'électricité.
            Un remplacement gaz → électrique économise en facture mais peut dégrader le DPE.
          </p>
        </div>
      </div>
    </Card>
  );
}
